import { Button } from '@components/common';
import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';
import {
  AccountRecord,
  FinancialPostingRuleRecord,
  PaymentMethodRecord,
  TaxRateRecord,
} from '../../../../types/accounting';
import {
  formatPostingRuleMetadataSummary,
  getPostingRuleMetadataHighlights,
  getPostingRuleEventDefinition,
  getPostingRuleReferenceDefinition,
} from './postingRules';

interface FinancialPostingRulesTableProps {
  rules: FinancialPostingRuleRecord[];
  accounts: AccountRecord[];
  taxRates: TaxRateRecord[];
  paymentMethods: PaymentMethodRecord[];
  onEdit: (rule: FinancialPostingRuleRecord) => void;
  onDelete: (rule: FinancialPostingRuleRecord) => void;
}

export function FinancialPostingRulesTable({
  rules,
  accounts,
  taxRates,
  paymentMethods,
  onEdit,
  onDelete,
}: FinancialPostingRulesTableProps) {
  const accountLookup = new Map(
    accounts.map((account) => [account.id, account]),
  );
  const taxRateLookup = new Map(
    taxRates.map((taxRate) => [taxRate.id, taxRate]),
  );
  const paymentMethodLookup = new Map(
    paymentMethods.map((method) => [method.id, method]),
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[32rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Rule
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Source
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Direction
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Account
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Conditions
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => {
              const account = accountLookup.get(rule.account_id);
              const taxRate = rule.tax_rate_id
                ? taxRateLookup.get(rule.tax_rate_id)
                : null;
              const paymentMethod = rule.payment_method_id
                ? paymentMethodLookup.get(rule.payment_method_id)
                : null;
              const eventDefinition = getPostingRuleEventDefinition(
                rule.source_module,
                rule.event_type,
              );
              const referenceDefinition = rule.reference_type
                ? getPostingRuleReferenceDefinition(
                    rule.source_module,
                    rule.reference_type,
                  )
                : null;
              const metadataSummary = formatPostingRuleMetadataSummary(
                rule.source_module,
                rule.event_type,
                rule.metadata,
              );
              const metadataHighlights = getPostingRuleMetadataHighlights(
                rule.source_module,
                rule.event_type,
                rule.metadata,
              );

              return (
                <tr key={rule.id} className="border-t border-gray-100">
                  <td className="px-5 py-4">
                    <div className="font-medium text-gray-900">
                      {rule.rule_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {eventDefinition?.label || rule.event_type}
                      {rule.reference_type
                        ? ` · ${
                            referenceDefinition?.label || rule.reference_type
                          }`
                        : ''}
                    </div>
                  </td>
                  <td className="px-5 py-4 capitalize text-gray-600">
                    {rule.source_module.replace('_', ' ')}
                  </td>
                  <td className="px-5 py-4 capitalize text-gray-600">
                    {rule.direction}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {account
                      ? `${account.code} - ${account.name}`
                      : rule.account_id}
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-600">
                    <div>Priority: {rule.priority}</div>
                    <div className="mt-1">
                      Tax:{' '}
                      {taxRate ? `${taxRate.code} - ${taxRate.name}` : 'Any'}
                    </div>
                    <div className="mt-1">
                      Payment: {paymentMethod ? paymentMethod.name : 'Any'}
                    </div>
                    {metadataHighlights.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {metadataHighlights.map((highlight) => (
                          <span
                            key={`${rule.id}-${highlight.key}`}
                            className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700"
                          >
                            {highlight.label}: {highlight.value}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {metadataSummary.map((summaryItem) => (
                      <div key={summaryItem} className="mt-1">
                        {summaryItem}
                      </div>
                    ))}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                        rule.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {rule.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onEdit(rule)}
                      >
                        <Pencil size={14} />
                        Edit
                      </Button>
                      <Button
                        variant="text"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(rule)}
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
